import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/app/Layout";
import { toast, Toaster } from "sonner";
import { Plus, Archive, RotateCcw, Pencil, Trash2, FolderOpen, Clock, CheckCircle2, Send, Edit3, CalendarRange } from "lucide-react";

const STATUS_META = {
  active: { label: "Brouillon", cls: "bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-gray-300", icon: Edit3 },
  ready_for_review: { label: "En attente", cls: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300", icon: Clock },
  approved: { label: "Validé", cls: "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300", icon: CheckCircle2 },
  rejected: { label: "À corriger", cls: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300", icon: Send },
  archived: { label: "Archivé", cls: "bg-gray-200 text-gray-600 dark:bg-neutral-700 dark:text-gray-300", icon: Archive },
};

export default function Reports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [month, setMonth] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filter, setFilter] = useState("active"); // active | archived

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // "active" tab: show everything that isn't archived (drafts, submitted, approved, rejected)
      const { data } = await api.get("/reports");
      const list = (data || []).filter((r) => filter === "archived" ? r.status === "archived" : r.status !== "archived");
      setReports(list);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Chargement impossible";
      setLoadError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reports", {
        name: name.trim(),
        month: month || null,
        date_from: dateFrom || null,
        date_to: dateTo || null,
      });
      setShowCreate(false); setName(""); setMonth(""); setDateFrom(""); setDateTo("");
      toast.success("Rapport créé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  // When the user picks a month, auto-fill date_from / date_to with first→last day
  // (they can still override). This makes Excel header dates match what they expect by default.
  const onMonthChange = (val) => {
    setMonth(val);
    if (val && /^\d{4}-\d{2}$/.test(val)) {
      const [y, m] = val.split("-").map(Number);
      const last = new Date(y, m, 0).getDate();
      setDateFrom(`${val}-01`);
      setDateTo(`${val}-${String(last).padStart(2, "0")}`);
    }
  };

  const rename = async (r) => {
    const newName = window.prompt("Nouveau nom", r.name);
    if (!newName) return;
    try {
      await api.put(`/reports/${r.report_id}`, { name: newName });
      toast.success("Renommé");
      load();
    } catch { toast.error("Échec"); }
  };

  const toggleArchive = async (r) => {
    const newStatus = r.status === "archived" ? "active" : "archived";
    try {
      await api.put(`/reports/${r.report_id}`, { status: newStatus });
      toast.success(newStatus === "archived" ? "Archivé" : "Réactivé");
      load();
    } catch { toast.error("Échec"); }
  };

  const editDates = async (r) => {
    const df = window.prompt("Date de début (YYYY-MM-DD), ou laisser vide pour auto :", r.date_from || "");
    if (df === null) return;
    const dt = window.prompt("Date de fin (YYYY-MM-DD), ou laisser vide pour auto :", r.date_to || "");
    if (dt === null) return;
    try {
      await api.put(`/reports/${r.report_id}`, {
        date_from: df.trim() || null,
        date_to: dt.trim() || null,
      });
      toast.success("Dates mises à jour");
      load();
    } catch { toast.error("Échec"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer "${r.name}" ? Les factures seront détachées mais pas supprimées.`)) return;
    try {
      await api.delete(`/reports/${r.report_id}`);
      toast.success("Rapport supprimé");
      load();
    } catch { toast.error("Échec"); }
  };

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="overline mb-2">Rapports</p>
          <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-tight sm:leading-none">
            Vos comptes de dépenses.
          </h1>
          <p className="text-gray-500 mt-3 max-w-lg">Créez un rapport mensuel, groupez vos factures, archivez quand c'est soumis.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)} data-testid="new-report-button">
          <Plus size={16} /> Nouveau rapport
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-gray-200">
        {[
          { k: "active", l: "Actifs" },
          { k: "archived", l: "Archivés" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${filter === t.k ? "border-[#002FA7] text-[#0B0F19]" : "border-transparent text-gray-500 hover:text-[#0B0F19]"}`}
            data-testid={`filter-${t.k}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {loadError && (
        <div className="ra-card p-5 mb-6 border-l-4 !border-l-red-500 flex items-center justify-between gap-3" data-testid="reports-load-error">
          <div>
            <p className="overline text-red-600">Erreur de chargement</p>
            <div className="text-sm mt-1">{loadError}</div>
          </div>
          <button className="btn-primary" onClick={load} data-testid="reports-retry">Réessayer</button>
        </div>
      )}

      {showCreate && (
        <form onSubmit={create} className="ra-card p-5 mb-6" data-testid="create-report-form">
          <p className="overline mb-3">Nouveau rapport</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="ra-label">Nom</label>
              <input className="ra-input" placeholder="Ex: Novembre 2025" value={name} onChange={(e) => setName(e.target.value)} required data-testid="input-name" />
            </div>
            <div>
              <label className="ra-label">Mois (optionnel)</label>
              <input type="month" className="ra-input" value={month} onChange={(e) => onMonthChange(e.target.value)} data-testid="input-month" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="ra-label">Date de début (sur l'Excel)</label>
              <input type="date" className="ra-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="input-date-from" />
            </div>
            <div>
              <label className="ra-label">Date de fin (sur l'Excel)</label>
              <input type="date" className="ra-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="input-date-to" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">📅 Les dates affichées en haut de l'Excel. Pré-remplies automatiquement quand vous choisissez un mois (1er → dernier jour).</p>
          <div className="mt-4 flex gap-2">
            <button type="submit" className="btn-primary" data-testid="submit-create">Créer</button>
            <button type="button" className="btn-secondary" onClick={() => setShowCreate(false)}>Annuler</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reports-grid">
        {loading && <div className="progress-line col-span-full" />}
        {!loading && reports.length === 0 && (
          <div className="col-span-full text-center p-12 text-gray-400">
            Aucun rapport {filter === "archived" ? "archivé" : filter === "active" ? "actif" : ""}.
          </div>
        )}
        {reports.map((r) => {
          const meta = STATUS_META[r.status] || STATUS_META.active;
          const Icon = meta.icon;
          return (
            <div key={r.report_id} className="ra-card p-5" data-testid={`report-card-${r.report_id}`}>
              <div className="flex items-start justify-between mb-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${meta.cls}`}>
                  <Icon size={10} /> {meta.label}
                </span>
                <div className="flex items-center gap-1">
                  <button className="btn-ghost" onClick={() => rename(r)} title="Renommer" data-testid={`rename-${r.report_id}`}>
                    <Pencil size={14} />
                  </button>
                  <button className="btn-ghost" onClick={() => editDates(r)} title="Modifier les dates" data-testid={`edit-dates-${r.report_id}`}>
                    <CalendarRange size={14} />
                  </button>
                  <button className="btn-ghost" onClick={() => toggleArchive(r)} title={r.status === "archived" ? "Réactiver" : "Archiver"} data-testid={`archive-${r.report_id}`}>
                    {r.status === "archived" ? <RotateCcw size={14} /> : <Archive size={14} />}
                  </button>
                  <button className="btn-ghost text-red-600 hover:bg-red-50" onClick={() => remove(r)} title="Supprimer" data-testid={`delete-report-${r.report_id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="font-heading font-black text-2xl tracking-tighter mb-2">{r.name}</div>
              <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                {r.month && <span className="num">{r.month}</span>}
                {(r.date_from || r.date_to) && (
                  <>
                    <span>·</span>
                    <span className="num text-xs" title="Dates manuelles affichées sur l'Excel">
                      📅 {r.date_from || "auto"} → {r.date_to || "auto"}
                    </span>
                  </>
                )}
                <span>·</span>
                <span>{r.invoice_count || 0} factures</span>
              </div>
              {r.status === "rejected" && r.review_comment && (
                <div className="mb-3 text-xs text-amber-800 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-200 p-2 rounded border-l-2 border-amber-400">
                  <strong>Notes de l'admin :</strong> {r.review_comment}
                </div>
              )}
              <div className="overline mb-1">Total</div>
              <div className="num font-heading text-3xl font-black tracking-tight mb-4">
                {Number(r.total_sum || 0).toFixed(2)} $
              </div>
              <button
                className="btn-secondary w-full justify-center"
                onClick={() => navigate(`/dashboard?report=${r.report_id}`)}
                data-testid={`open-${r.report_id}`}
              >
                <FolderOpen size={14} /> Ouvrir
              </button>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
